import { useEffect, useState } from 'react';

import { openAppSettingsStore, type AppSettingsStore, type LocalDataCounts } from './settingsStore';

/**
 * 打开应用设置的存储。
 *
 * `openPersistence` **永不抛错**（最坏降级到内存后端），所以这里没有失败分支 ——
 * 有的只是"这次能不能真的保存"这一条提示，由 `AppSettingsStore.durabilityNotice` 给出。
 */

export interface AppSettingsHandle {
  readonly store: AppSettingsStore | null;
  readonly counts: LocalDataCounts | null;
  readonly refreshCounts: () => Promise<void>;
  readonly loading: boolean;
}

const EMPTY_COUNTS: LocalDataCounts = Object.freeze({
  paramSnapshots: 0,
  preflightRecords: 0,
  auditEntries: 0,
});

export function useAppSettingsStore(): AppSettingsHandle {
  const [store, setStore] = useState<AppSettingsStore | null>(null);
  const [counts, setCounts] = useState<LocalDataCounts | null>(null);

  useEffect(() => {
    let active = true;
    let opened: AppSettingsStore | null = null;

    void openAppSettingsStore().then(async (next) => {
      if (!active) {
        await next.close();
        return;
      }
      opened = next;
      setStore(next);
      setCounts(await next.counts());
    });

    return () => {
      active = false;
      void opened?.close();
    };
  }, []);

  const refreshCounts = async (): Promise<void> => {
    if (store === null) return;
    setCounts(await store.counts());
  };

  return { store, counts: counts ?? (store === null ? null : EMPTY_COUNTS), refreshCounts, loading: store === null };
}
